import { useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import * as authApi from '../api/authApi';
import * as userApi from '../api/userApi';
import type { UserProfile, UserSummary } from '../api/types';

// UserProfile(UserController) → UserSummary(AuthController) 축약
function toSummary(p: UserProfile): UserSummary {
  return { id: p.id, email: p.email, name: p.name, avatarUrl: p.avatarUrl };
}

/**
 * 인증 동작 훅. authStore + API 호출 + (선택) navigate 를 조합한다.
 * Router context 가 필요 없는 동작(저장·복구·로그아웃 API)만 포함 — navigate 는 호출 측에서 처리.
 */
export function useAuth() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const bootstrapping = useAuthStore((s) => s.bootstrapping);
  const setSession = useAuthStore((s) => s.setSession);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setUser = useAuthStore((s) => s.setUser);
  const clear = useAuthStore((s) => s.clear);
  const setBootstrapping = useAuthStore((s) => s.setBootstrapping);

  const login = useCallback(
    async (email: string, password: string) => {
      const resp = await authApi.login({ email, password });
      setSession(resp.accessToken, resp.user);
      return resp;
    },
    [setSession]
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // 서버 실패해도 클라이언트 세션은 제거
    } finally {
      clear();
    }
  }, [clear]);

  /**
   * 앱 부트스트랩 시 1회 호출. refresh 쿠키로 access_token 복구 + /users/me 캐싱.
   * - 성공: authStore 채움
   * - 실패: authStore 비움 (로그인 페이지는 각자 Guard 에서 처리)
   */
  const bootstrap = useCallback(async () => {
    try {
      const r = await authApi.refresh();
      setAccessToken(r.accessToken);
      try {
        const me = await userApi.getCurrentUser();
        setUser(toSummary(me));
      } catch {
        // refresh 성공 but /me 실패 — 세션 무효화
        clear();
      }
    } catch {
      clear();
    } finally {
      setBootstrapping(false);
    }
  }, [setAccessToken, setUser, clear, setBootstrapping]);

  return {
    accessToken,
    user,
    bootstrapping,
    isAuthenticated: !!accessToken,
    login,
    logout,
    bootstrap,
  };
}
